/**
 * Assessment Detail Page
 *
 * Displays full details of a single assessment
 * Shows all the same information as the Results component
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Results from '../components/assessment/Results';

export default function AssessmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [loadingShare, setLoadingShare] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.contractor_id && id) {
      fetchAssessment();
    }
  }, [user, id]);

  const fetchAssessment = async () => {
    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from('assessments')
        .select(`
          *,
          customers (
            id,
            full_name,
            email,
            phone,
            property_address
          ),
          users!assessments_technician_id_fkey (
            id,
            full_name,
            email
          )
        `)
        .eq('id', id)
        .eq('contractor_id', user.contractor_id)
        .single();

      if (fetchError) throw fetchError;

      if (!data) {
        setError('Assessment not found or you do not have permission to view it.');
        return;
      }

      setAssessment(data);

      // Check if a share link already exists
      const { data: existingLink } = await supabase
        .from('assessment_links')
        .select('*')
        .eq('assessment_id', id)
        .single();

      if (existingLink) {
        const expiresAt = new Date(existingLink.expires_at);
        const now = new Date();
        if (existingLink.is_active && expiresAt > now) {
          setShareLink({
            url: `${window.location.origin}/share/${existingLink.share_token}`,
            expiresAt: existingLink.expires_at,
            viewCount: existingLink.view_count,
            lastViewedAt: existingLink.last_viewed_at
          });
        }
      }
    } catch (err) {
      console.error('Error fetching assessment:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShareLink = async () => {
    try {
      setLoadingShare(true);
      setShareError(null);

      const response = await fetch('/.netlify/functions/create-assessment-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assessmentId: id,
          userId: user.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create share link');
      }

      setShareLink({
        url: data.url,
        expiresAt: data.link.expires_at,
        viewCount: data.link.view_count,
        lastViewedAt: data.link.last_viewed_at
      });
    } catch (err) {
      console.error('Error creating share link:', err);
      setShareError(err.message);
    } finally {
      setLoadingShare(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setShareError('Failed to copy link to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading assessment...</div>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '10px' }}>
              {error || 'Assessment Not Found'}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '30px' }}>
              This assessment may not exist or you may not have permission to view it.
            </div>
            <Link to="/assessments" className="btn btn-primary">
              ← Back to Assessments
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reconstruct the state object from stored data
  const state = {
    ...assessment.system_data,
    customerName: assessment.customers?.full_name,
    customerEmail: assessment.customers?.email,
    customerPhone: assessment.customers?.phone,
    systemAge: assessment.system_age,
    repairCost: assessment.repair_cost
  };

  // Get results from analysis_result
  const results = assessment.analysis_result;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Back Button */}
        <div style={{ marginBottom: '20px' }}>
          <Link
            to="/assessments"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'white',
              border: '2px solid #e0e6ed',
              borderRadius: '8px',
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.color = '#667eea';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e0e6ed';
              e.target.style.color = '#64748b';
            }}
          >
            ← Back to Assessments
          </Link>
        </div>

        {/* Assessment Info Header */}
        <div className="card" style={{ marginBottom: '30px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', alignItems: 'start' }}>
            <div>
              <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#2c3e50' }}>
                {assessment.customers?.full_name}
              </h2>
              <div style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.8' }}>
                <div>
                  <strong>Assessment ID:</strong> {assessment.id.slice(0, 8)}...
                </div>
                <div>
                  <strong>Date:</strong> {new Date(assessment.created_at).toLocaleString()}
                </div>
                <div>
                  <strong>Technician:</strong> {assessment.users?.full_name}
                </div>
                {assessment.customers?.email && (
                  <div>
                    <strong>Email:</strong> {assessment.customers.email}
                  </div>
                )}
                {assessment.customers?.phone && (
                  <div>
                    <strong>Phone:</strong> {assessment.customers.phone}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
              <button
                onClick={() => window.print()}
                className="btn btn-secondary"
                style={{ fontSize: '14px' }}
              >
                🖨️ Print
              </button>
              {!shareLink ? (
                <button
                  onClick={handleCreateShareLink}
                  disabled={loadingShare}
                  className="btn btn-primary"
                  style={{ fontSize: '14px' }}
                >
                  {loadingShare ? '⏳ Creating...' : '🔗 Share Link'}
                </button>
              ) : (
                <button
                  onClick={handleCopyLink}
                  className="btn"
                  style={{
                    fontSize: '14px',
                    background: copied ? '#10b981' : '#667eea',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {copied ? '✅ Copied!' : '📋 Copy Link'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Share Link Card */}
        {shareLink && (
          <div className="card" style={{
            marginBottom: '30px',
            background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
            border: '2px solid #667eea'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🔗 Shareable Link
            </h3>

            <div style={{
              padding: '12px 16px',
              background: 'white',
              borderRadius: '6px',
              marginBottom: '15px',
              border: '1px solid #e0e6ed',
              fontSize: '14px',
              color: '#64748b',
              wordBreak: 'break-all',
              fontFamily: 'monospace'
            }}>
              {shareLink.url}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
              marginBottom: '15px',
              fontSize: '13px'
            }}>
              <div>
                <div style={{ color: '#64748b', marginBottom: '4px' }}>Expires</div>
                <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                  {new Date(shareLink.expiresAt).toLocaleDateString()}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748b', marginBottom: '4px' }}>Views</div>
                <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                  {shareLink.viewCount || 0}
                </div>
              </div>
              {shareLink.lastViewedAt && (
                <div>
                  <div style={{ color: '#64748b', marginBottom: '4px' }}>Last Viewed</div>
                  <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                    {new Date(shareLink.lastViewedAt).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>

            <div style={{
              padding: '12px',
              background: '#fffbeb',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#92400e',
              lineHeight: '1.5'
            }}>
              <strong>📌 Note:</strong> This link expires in {Math.ceil((new Date(shareLink.expiresAt) - new Date()) / (1000 * 60 * 60 * 24))} days.
              Anyone with this link can view the assessment without logging in.
            </div>
          </div>
        )}

        {/* Share Error */}
        {shareError && (
          <div className="card" style={{
            marginBottom: '30px',
            background: '#fee2e2',
            border: '2px solid #ef4444'
          }}>
            <strong style={{ color: '#991b1b' }}>Error:</strong> {shareError}
          </div>
        )}

        {/* Results Display - Same as Assessment Results */}
        {results && (
          <Results
            results={results}
            state={state}
            onReset={() => navigate('/assessment')}
            assessmentId={assessment.id}
            readOnly={true}
          />
        )}
      </div>
    </div>
  );
}
